/**
 * `useAskAssistant()` (TMMAPP-0151 §B, shared with Home's suggestions in TMMAPP-0152 §A 6): start a
 * **private** assistant session — `POST /v1/me/assistant/sessions` with `visibility: PRIVATE`, the
 * given scope and the registry's default categories — and open it at `/me/assistant/{id}`, the
 * composer prefilled with `question` when there is one. It never sends a message: the person sends
 * it, and pays only if they ask.
 *
 * The scope is kept to what the assistant can scope now (`GET …/options`); when none of it is, it is
 * sent as passed and the server's refusal reads as a notice. One request however often it's called.
 */
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { assistantApi, type AssistantScopeEntity } from '@/api/assistant';
import { errorCopy } from '@/api/errorCopy';
import { useNotify } from '@/shell/NotifyProvider';
import { askRouteState } from './askAbout';

export interface AskAssistantRequest {
  /** The session's name. */
  name: string;
  scope: readonly AssistantScopeEntity[];
  /** The composer's starting text; `null` opens an empty composer. */
  question: string | null;
}

const key = (entity: AssistantScopeEntity) => `${entity.kind}:${entity.id}`;

export function useAskAssistant(): {
  ask: (request: AskAssistantRequest) => Promise<void>;
  busy: boolean;
} {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const notify = useNotify();
  const [busy, setBusy] = useState(false);
  const starting = useRef(false);

  async function ask({ name, scope, question }: AskAssistantRequest) {
    if (starting.current) return;
    starting.current = true;
    setBusy(true);
    try {
      const options = await assistantApi.options();
      const scopable = new Set(options.scopable.map(key));
      const reachable = scope.filter((entity) => scopable.has(key(entity)));
      const categories = Object.fromEntries(
        options.categories.map((option) => [option.key, option.defaultEnabled]),
      );
      const session = await assistantApi.create({
        name,
        scope: reachable.length > 0 ? reachable : [...scope],
        categories,
        visibility: 'PRIVATE',
      });
      void navigate(
        `/me/assistant/${session.id}`,
        question === null ? undefined : { state: askRouteState(question) },
      );
    } catch (error) {
      notify(errorCopy(t, error, 'assistant.error.generic'), { severity: 'error' });
    } finally {
      starting.current = false;
      setBusy(false);
    }
  }

  return { ask, busy };
}
